import { z } from 'zod';
import { Currency } from '../enums.js';
import { DecimalString, IsoDate, IsoDateTime, MoneyAmount, UuidString } from '../primitives.js';
import { UomCode } from './uom-code.js';

/**
 * S14-BE.1 — Invoice (AR/AP polymorphic через discriminator kind).
 * ERPNext pattern — Sales Invoice + Purchase Invoice объединены в одной таблице.
 * Lines относительны, отдельная таблица `invoice_lines`.
 */

export const InvoiceKind = z.enum(['ar', 'ap']);
export type InvoiceKind = z.infer<typeof InvoiceKind>;

export const InvoiceState = z.enum([
  'draft',
  'pending_approval',
  'posted',
  'partially_paid',
  'paid',
  'cancelled',
  'void',
  /** B74 (Ф6, §9.4 п. 7): идёт отправка в 1С по заявке уровня 3 — повторная не пройдёт. */
  'pushing',
]);
export type InvoiceState = z.infer<typeof InvoiceState>;

export const VatRate = z.enum(['0', '12', 'exempt']);
export type VatRate = z.infer<typeof VatRate>;

// B6 invariant — entity-уровень response-схемы forward-compat (см. zod-strict.test.ts).
export const InvoiceLine = z.object({
  line_no: z.number().int().min(1),
  item_id: UuidString.nullable(),
  description_ru: z.string().min(1),
  description_kk: z.string().nullable(),
  qty: DecimalString,
  uom_code: UomCode,
  unit_price: MoneyAmount,
  line_subtotal: MoneyAmount,
  vat_rate: VatRate,
  vat_amount: MoneyAmount,
  line_total: MoneyAmount,
});
export type InvoiceLine = z.infer<typeof InvoiceLine>;

export const Invoice = z.object({
  id: UuidString,
  entity_id: UuidString,
  kind: InvoiceKind,
  state: InvoiceState,
  counterparty_id: UuidString,
  document_number: z.string(),
  issue_date: IsoDate,
  due_date: IsoDate,
  currency: Currency,
  fx_rate_to_kzt: DecimalString.default('1'),
  lines: z.array(InvoiceLine).min(1),
  subtotal: MoneyAmount,
  vat_total: MoneyAmount,
  total: MoneyAmount,
  paid_amount: MoneyAmount,
  outstanding_amount: MoneyAmount,
  work_order_id: UuidString.nullable(),
  landed_cost_voucher_id: UuidString.nullable(),
  stock_entry_id: UuidString.nullable(),
  notes_ru: z.string().nullable(),
  onec_ref: z.string().nullable(),
  created_at: IsoDateTime,
  // EFE-13/14: updated_at для optimistic concurrency + detail page footer.
  updated_at: IsoDateTime.optional(),
  posted_at: IsoDateTime.nullable(),
  pushed_at: IsoDateTime.nullable(),
  created_by: UuidString,
  posted_by: UuidString.nullable(),
});
export type Invoice = z.infer<typeof Invoice>;
